import { HStack, PageContainer } from '@/components/Atoms'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PageLoader } from '@/components/PageLoader'
import { useLaunchValidation } from '@/hooks/launches/useLaunchValidation'
import {
  launchDetailsQueryOptions,
  launchDetailsSchema,
  useLaunchDetails,
  useUpdateLaunch,
} from '@/hooks/queries/useLaunches'
import { displayErrorToast } from '@/lib/utils/toasters'
import { queryClient } from '@/main'
import type { LaunchDetails, LaunchErp } from '@repo/backend/assortment'
import { toastClassName } from '@repo/ui/lib/constants'
import { getUserFullname } from '@repo/utils'
import { createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { LaunchValidationDialog } from './-components/LaunchDetails/Dialogs/LaunchValidationDialog'
import { ActiveSwitch } from '@/components/DataTable/ActiveSwitch'
import { LaunchErpsTable } from './-components/LaunchDetails/LaunchErpsTable'
import { DetailsHeader } from '@/components/DetailsHeader'
import {
  LaunchMaterialsTable,
  LaunchMaterialTableRow,
} from './-components/LaunchDetails/LaunchMaterialsTable'
import { PendingChanges } from '@/components/PendingChanges'
import { PublishStatusBanner } from '@/components/PublishStatusBanner'
import { usePublishStatus } from '@/hooks/queries/useBcpPublishStatus'

export const Route = createFileRoute('/_auth/_modules/_app/assortment/launches/$launchId')({
  component: LaunchDetails,
  loader: async ({ params: { launchId } }) => {
    const launch = await queryClient.ensureQueryData(launchDetailsQueryOptions(launchId))

    return {
      crumb: launch.launchName,
    }
  },
})

function LaunchDetails() {
  const { t } = useTranslation(['assortment', 'common'])
  const { launchId } = Route.useParams()
  const { isPending, error, data: initialLaunch } = useLaunchDetails(launchId)
  const { isPublishing: isPublishActive } = usePublishStatus('launches')

  const [launch, setLaunch] = useState<LaunchDetails | undefined>(initialLaunch)
  const [areLaunchDetailsPending, setAreLaunchDetailsPending] = useState(false)
  const [materials, setMaterials] = useState<LaunchMaterialTableRow[]>(
    initialLaunch?.materials ?? [],
  )
  const [newErps, setNewErps] = useState<LaunchErp[]>([])

  // Sync local state when server data changes
  const [prevInitialLaunch, setPrevInitialLaunch] = useState(initialLaunch)
  if (initialLaunch !== prevInitialLaunch) {
    setLaunch(initialLaunch)
    setMaterials(initialLaunch?.materials ?? [])
    setPrevInitialLaunch(initialLaunch)
  }

  const newMaterials = materials.filter((material) => !material.launchMaterialId)

  const erps = [...(launch?.erps ?? []), ...newErps].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? ''),
  )

  const areChangesPending =
    areLaunchDetailsPending ||
    materials.some((m) => m.isUnsaved) ||
    newMaterials.length > 0 ||
    newErps.length > 0

  const {
    conflicts,
    entityCategoryMismatches,
    isValidationPending,
    setIsValidationPending,
    isValidationDialogOpen,
    setIsValidationDialogOpen,
    validate,
  } = useLaunchValidation(+launchId, erps, materials)

  const { isPending: isUpdateLaunchPending, mutate: updateLaunch } = useUpdateLaunch({
    launchId: launch?.launchId ?? 0,
    details: {
      launchName: launch?.launchName ?? '',
      launchDescr: launch?.launchDescr ?? '',
    },
    materials: materials.filter((m) => m.isUnsaved),
    newErps: newErps.map((e) => e.sapErp ?? ''),
    isActive: launch?.isActive ?? false,
    handleOnSuccess: () => {
      setAreLaunchDetailsPending(false)
      setMaterials(launch?.materials ?? [])
      setNewErps([])
    },
  })

  const handleUpdateName = (name: string) => {
    const result = launchDetailsSchema.pick({ launchName: true }).safeParse({ launchName: name })
    if (result.error) {
      displayErrorToast(result.error.issues[0].message)
      return
    }

    if (launch) {
      setLaunch({ ...launch, launchName: name })
    }
    setAreLaunchDetailsPending(true)
  }

  const handleUpdateDescription = (description: string) => {
    if (launch) {
      setLaunch({ ...launch, launchDescr: description })
    }
    setAreLaunchDetailsPending(true)
  }

  const handleSave = async () => {
    if (isUpdateLaunchPending || isValidationPending) return

    setIsValidationPending(true)

    try {
      const { results: conflicts, entityCategoryMismatches } = await validate()

      if (conflicts.length > 0 || entityCategoryMismatches.length > 0) {
        setIsValidationDialogOpen(true)
      } else {
        setIsValidationDialogOpen(false)
        updateLaunch()
      }
    } catch {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('launchConflictsDialog.scanError'),
        className: toastClassName,
      })
    }

    setIsValidationPending(false)
  }

  if (isPending || !launch) return <PageLoader />
  if (error) return `An error has occurred: ${error.message}`

  return (
    <PageContainer className="pb-28">
      <PublishStatusBanner publishType="launches" />
      <HStack gap="sm" className="justify-between">
        <DetailsHeader
          name={launch.launchName ?? ''}
          description={launch.launchDescr ?? ''}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="assortment_launches_edit"
          isDisabled={isPublishActive}
        />
        <ActiveSwitch
          isActive={launch.isActive ?? false}
          onUpdateIsActive={(isActive) => {
            setLaunch({ ...launch, isActive })
            setAreLaunchDetailsPending(true)
          }}
          permission="assortment_launches_edit"
          isDisabled={isPublishActive}
        />
      </HStack>

      <LaunchMaterialsTable
        launch={launch}
        materials={materials}
        setMaterials={setMaterials}
        isPublishing={isPublishActive}
      />

      <LaunchErpsTable
        launchId={launch.launchId ?? 0}
        erps={erps}
        setNewErps={setNewErps}
        isPublishing={isPublishActive}
      />

      <PermissionsGuard required={['assortment_launches_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          isSavePending={isUpdateLaunchPending || isValidationPending}
          isDisabled={isPublishActive}
          lastUpdatedLog={
            launch.log && launch.log.updatedAt && launch.log.user
              ? {
                  formattedDate: format(launch.log.updatedAt, "MMM dd, yyyy 'at' hh:mm a"),
                  userFullName: getUserFullname(launch.log.user),
                }
              : undefined
          }
          onSaveChanges={handleSave}
        />
        <LaunchValidationDialog
          conflicts={conflicts}
          entityCategoryMismatches={entityCategoryMismatches}
          isValidationPending={isValidationPending}
          setIsValidationPending={setIsValidationPending}
          isValidationDialogOpen={isValidationDialogOpen}
          setIsValidationDialogOpen={setIsValidationDialogOpen}
          isUpdateLaunchPending={isUpdateLaunchPending}
          validate={validate}
          onSave={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
